import React from 'react';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { PROTOCOL_COLORS, CHAIN_COLORS } from '../data';
import { useTheme } from '../theme';
import { InfoIcon } from './Tooltip';
import TOOLTIP_CONTENT from '../tooltipContent';
import { useIsMobile } from '../useMediaQuery';

export default function FilterBar({
  search, setSearch,
  stablesOnly, setStablesOnly,
  chain, setChain,
  protocol, setProtocol,
  sortBy, setSortBy,
  sortDir,
  pools,
  showAnomalous, onToggleAnomalous,
  searchInputRef,
  onExportCSV, onExportJSON,
}) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  const pillStyle = (active) => ({
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: isMobile ? 12 : 11,
    padding: isMobile ? "8px 14px" : "6px 14px",
    borderRadius: 20,
    border: `1px solid ${active ? colors.activeBg : colors.border}`,
    background: active ? colors.activeBg : colors.pillBg,
    color: active ? colors.activeText : colors.textSecondary,
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.15s",
    flexShrink: 0,
  });

  // Derive chains and protocols from live pool data
  const chains = React.useMemo(() => {
    if (!pools || pools.length === 0) return ["All"];
    const unique = [...new Set(pools.map(p => p.chain))].sort();
    return ["All", ...unique];
  }, [pools]);

  const protocols = React.useMemo(() => {
    if (!pools || pools.length === 0) return ["All"];
    const unique = [...new Set(pools.map(p => p.protocol))].sort();
    return ["All", ...unique];
  }, [pools]);
  const sorts = [
    { key: "popular", label: "POPULAR" },
    { key: "apr", label: "APR" },
    { key: "tvl", label: "TVL" },
    { key: "volume24h", label: "VOL 24H" },
    { key: "fees24h", label: "FEES 24H" },
  ];

  return (
    <div style={{
      padding: isMobile ? "12px 12px" : "16px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 8 : 12,
        flexWrap: "wrap",
        flexDirection: isMobile ? "column" : "row",
        ...(isMobile ? { alignItems: "stretch" } : {}),
      }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search pools or tokens... ( / )"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            padding: "8px 14px",
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            background: colors.inputBg,
            color: colors.textPrimary,
            width: isMobile ? '100%' : 220,
            outline: "none",
            transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
          }}
        />

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => setStablesOnly(!stablesOnly)}
            style={{
              ...pillStyle(stablesOnly),
            }}
          >
            Stables Only
          </button>
          <InfoIcon text={TOOLTIP_CONTENT.STABLECOIN} position="bottom" colors={colors} />
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
          <button
            onClick={onToggleAnomalous}
            title={showAnomalous ? "Showing all pools including anomalous APR" : "Some pools with anomalous APR are hidden"}
            style={{
              ...pillStyle(showAnomalous),
              ...(!showAnomalous ? {
                background: '#FFF8E0',
                borderColor: '#E0CC70',
                color: '#8A6A00',
              } : {}),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {!showAnomalous && (
              <span style={{ fontSize: 12, lineHeight: 1 }}>&#x26A0;</span>
            )}
            {showAnomalous ? "Filtered" : "Show All APR"}
          </button>
          {!showAnomalous && (
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: '#8A6A00',
              whiteSpace: 'nowrap',
            }}>
              anomalous hidden
            </span>
          )}
        </span>

        {!isMobile && <div style={{ width: 1, height: 24, background: colors.border }} />}

        <div style={{
          display: "flex",
          gap: 8,
          flexWrap: isMobile ? "nowrap" : "wrap",
          ...(isMobile ? { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' } : {}),
        }}>
          {chains.map(c => (
            <button key={c} onClick={() => setChain(c)} style={{
              ...pillStyle(chain === c),
              ...(c !== "All" && chain === c ? {
                background: CHAIN_COLORS[c],
                borderColor: CHAIN_COLORS[c],
                color: "#FFFFFF",
              } : {}),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}>
              {c !== "All" && <ChainIcon chain={c} size={12} />}
              {c}
            </button>
          ))}
        </div>

        {!isMobile && <div style={{ width: 1, height: 24, background: colors.border }} />}

        <div style={{
          display: "flex",
          gap: 8,
          flexWrap: isMobile ? "nowrap" : "wrap",
          ...(isMobile ? { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' } : {}),
        }}>
          {protocols.map(p => (
            <button key={p} onClick={() => setProtocol(p)} style={{
              ...pillStyle(protocol === p),
              ...(p !== "All" && protocol === p ? {
                background: PROTOCOL_COLORS[p],
                borderColor: PROTOCOL_COLORS[p],
                color: "#FFFFFF",
              } : {}),
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}>
              {p !== "All" && <ProtocolIcon protocol={p} size={12} />}
              {p}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1,
        }}>
          SORT BY
        </span>
        {sorts.map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)} style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            padding: "4px 10px",
            borderRadius: 4,
            border: `1px solid ${sortBy === s.key ? colors.activeBg : colors.border}`,
            background: sortBy === s.key ? colors.activeBg : "transparent",
            color: sortBy === s.key ? colors.activeText : colors.labelMuted,
            cursor: "pointer",
            fontWeight: 500,
          }}>
            {s.label}
            {sortBy === s.key && (
              <span style={{ marginLeft: 4, fontSize: 8 }}>
                {sortDir === 'asc' ? '▲' : '▼'}
              </span>
            )}
          </button>
        ))}
        {(onExportCSV || onExportJSON) && !isMobile && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {onExportCSV && (
              <button onClick={onExportCSV} style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.labelMuted,
                cursor: "pointer",
                fontWeight: 500,
              }}>
                CSV ↓
              </button>
            )}
            {onExportJSON && (
              <button onClick={onExportJSON} style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${colors.border}`,
                background: "transparent",
                color: colors.labelMuted,
                cursor: "pointer",
                fontWeight: 500,
              }}>
                JSON ↓
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
